/**
 * Normalises Authorize.Net payment failures returned by
 * PaymentChannel/AuthNetOneTimePayment into one shape the UI can act on.
 *
 * Backend contract: on failure the body is
 *   { meta: { errors: { errorCode, errorMessages: [{ field, message }], transactionId?, avsResultCode? } } }
 * and errorCode === 'AVS_MISMATCH' when the billing address / ZIP did not
 * match the card issuer's records (Enhanced AVS filter, reason code 27).
 *
 * The API sometimes forwards Authorize.Net's raw payload (JSON and/or the
 * comma-separated validationDirectResponse). This module turns that into a
 * short, plain-English sentence plus a reference code for support.
 */

export const ADDRESS_MISMATCH_CODE = 'AVS_MISMATCH';

export const ADDRESS_MISMATCH_MESSAGE =
  'The billing address or ZIP code does not match the address your card issuer has on file. ' +
  'Please check the Billing ZIP Code on this page and, if your street address has changed, update it in your profile ' +
  'so both match your card statement. Then try the payment again.';

export const PROFILE_PATH = '/profile';

const GENERIC_MESSAGE = 'Payment failed. Please try again.';

const PROVIDER_GENERIC_MESSAGE =
  'Our payment provider could not process this payment. Please check your card details and try again, ' +
  'or use a different card.';

// Older API builds (and the payment-profile validation path) only expose the raw text.
const ADDRESS_MISMATCH_TEXT = /AVS[_ ]MISMATCH|AVS mismatch|does not match billing address of cardholder/i;

/**
 * Plain-English replacements for Authorize.Net API (Exxxxx) and transaction
 * reason codes we have seen in production. Unknown codes fall back to the
 * provider's own text (already fairly readable) or a generic message.
 */
const FRIENDLY_MESSAGES = {
  // Customer Information Manager (CIM) limits
  E00042:
    'Our payment provider already holds the maximum number of saved cards (10) for your account, so a new card cannot be added. ' +
    'Please use a card you have paid with here before, or contact the temple office so we can clear old cards from your account.',
  E00039: 'A payment profile for this card already exists. Please try the payment again.',
  E00040: 'We could not find your saved payment details. Please try again or use a different card.',
  E00027: 'The transaction was declined by your card issuer. Please check your card details or try a different card.',
  E00003: 'Some of the card details were not accepted. Please check the card number, expiry date and ZIP code, and try again.',
  E00013: 'Some of the card details were not accepted. Please check the card number, expiry date and ZIP code, and try again.',
  E00007: 'There is a problem with our payment gateway configuration. Please contact the temple office.',
  E00001: 'Our payment provider is temporarily unavailable. Please try again in a few minutes.',
  // Transaction reason codes (validationDirectResponse field 3 / transactionResponse.errors[].errorCode)
  2: 'The transaction was declined by your card issuer. Please check your card details or try a different card.',
  3: 'The transaction was declined by your card issuer. Please check your card details or try a different card.',
  4: 'Your card issuer asked us to hold this card. Please contact your bank or use a different card.',
  6: 'The card number is invalid. Please check it and try again.',
  7: 'The card expiry date is invalid. Please check it and try again.',
  8: 'This card has expired. Please use a different card.',
  11: 'A duplicate transaction was submitted. Please wait a moment before trying again.',
  27: ADDRESS_MISMATCH_MESSAGE,
  44: 'The card security code (CVV) was not accepted. Please check it and try again.',
  45: 'The card security code or billing address was not accepted. Please check them and try again.',
  65: 'The card security code (CVV) was not accepted. Please check it and try again.',
  78: 'The card security code (CVV) is invalid. Please check it and try again.',
  250: 'This transaction was blocked by our payment provider\u2019s fraud filter. Please contact the temple office.',
  251: 'This transaction was blocked by our payment provider\u2019s fraud filter. Please contact the temple office.',
};

const stripCodePrefix = (message) => message.replace(/^[A-Z_]+:\s*/, '');

const looksTechnical = (text) =>
  /validationDirectResponse|"resultCode"|"messages"|"errorCode"|\{\s*"|,,,,|Response:|Failed to create/i.test(text);

/** Pulls { code, text } out of an Authorize.Net JSON fragment embedded in a string. */
const extractApiMessage = (raw) => {
  const match = raw.match(/"code"\s*:\s*"(E\d{5})"\s*,\s*"text"\s*:\s*"((?:[^"\\]|\\.)*)"/);
  return match ? { code: match[1], text: match[2].replace(/\\"/g, '"') } : null;
};

/** Pulls { code, text } out of a validationDirectResponse CSV: "2,2,27,The transaction has been declined...". */
const extractDirectResponse = (raw) => {
  const match = raw.match(/validationDirectResponse[^"]*"\s*:\s*"(\d),(\d+),(\d+),([^,]*)/);
  return match ? { code: match[3], text: match[4].trim() } : null;
};

/**
 * Converts whatever the API sent into a short user-facing sentence.
 * @returns {{ message: string, referenceCode: string|null }}
 */
export const humanizePaymentMessage = (raw, apiCode = null) => {
  const text = String(raw ?? '').trim();

  const embedded = extractApiMessage(text) ?? extractDirectResponse(text);
  const referenceCode = embedded?.code ?? (apiCode && /^(E\d{5}|\d{1,3})$/.test(apiCode) ? apiCode : null);

  const friendly = FRIENDLY_MESSAGES[referenceCode];
  if (friendly) return { message: friendly, referenceCode };

  if (embedded?.text) return { message: embedded.text.replace(/\.?$/, '.'), referenceCode };

  if (!text) return { message: GENERIC_MESSAGE, referenceCode };
  if (looksTechnical(text)) return { message: PROVIDER_GENERIC_MESSAGE, referenceCode };

  return { message: stripCodePrefix(text), referenceCode };
};

/**
 * @param {unknown} source  API response body, axios error, Error, or an already-extracted message string
 * @returns {{ code: string|null, message: string, referenceCode: string|null, isAddressMismatch: boolean, transactionId: string|null }}
 */
export const getPaymentError = (source) => {
  const body = source?.response?.data ?? source;
  const errors = body?.meta?.errors;

  const code = errors?.errorCode ?? null;
  const rawMessage =
    (typeof source === 'string' ? source : null) ??
    errors?.errorMessages?.[0]?.message ??
    body?.message ??
    body?.createTransactionResponse?.transactionResponse?.errors?.[0]?.errorText ??
    body?.createTransactionResponse?.transactionResponse?.messages?.[0]?.description ??
    (source instanceof Error ? source.message : null) ??
    GENERIC_MESSAGE;

  const { message, referenceCode } = humanizePaymentMessage(rawMessage, code);
  const isAddressMismatch =
    code === ADDRESS_MISMATCH_CODE || referenceCode === '27' || ADDRESS_MISMATCH_TEXT.test(rawMessage);

  return {
    code: isAddressMismatch ? ADDRESS_MISMATCH_CODE : code,
    message: isAddressMismatch ? ADDRESS_MISMATCH_MESSAGE : message,
    referenceCode: isAddressMismatch ? null : referenceCode,
    isAddressMismatch,
    transactionId: errors?.transactionId ?? null,
  };
};
